import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';
import '../../voyage-popup/src/voyage-popup';
import { VoyTimeinput } from '../../voy-timeinput/src/voy-timeinput';
import '../../voy-timeinput/src/voy-timeinput';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { deepCopy, deepEqual } from '../../../src/utils/helpers';
import { recalcRoutes } from '../../../src/utils/route-utils';
import { verifyETAOverlapWithDepartureTime } from '../../../src/utils/helpers';
import { VoyageRoutePointFeature, VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { ScheduleEntry } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';

@customElement('voy-wp-eta-popup')
export class VoyWPEtaPopup extends LitElement
{
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Number}) wpPlanIndex: number = -1;

    static get styles()
    {
        return [
            css`
            :host {
            }

            .container {
                display: flex;
                margin: 15px;
                align-items: center;
            }

            .remove {
                color: var(--waypoint-editor-remove-button-color);
                background-color: transparent;
                border: none;
                cursor: pointer;
            }
            `
        ];
    }

    render()
    {
        return html`
        <voyage-popup accept-button-text="Save" .shown=${this.shown} header-text="Waypoint ETA"
                      @accept=${this._applyChange}
                      @close=${() => this.dispatchEvent(new Event('close'))}
                      .disableAcceptButton=${!this._allowEmptyInput ? this._inputValue === -1 : false}
                      .loading=${this._working}>
            <div class="container">
                <voy-timeinput .value=${this._inputValue} warn-utc
                               .label=${"ETA"}
                               .terminalId=${this._terminalId}
                               .allowEmpty=${this._allowEmptyInput}
                               @input=${(evt:InputEvent) => this._inputValue = (<VoyTimeinput>evt.target).value }>
                </voy-timeinput>
            </div>
        </voyage-popup>
        `;
    }

    updated(changedProperties: PropertyValues)
    {
        if ((changedProperties.has("shown") || changedProperties.has("wpPlanIndex")) && this.shown)
        {
            this._readScheduleAndPlan();
        }
    }

    @property({ attribute: false }) private _terminalId: string = "";
    @property({ attribute: false }) private _inputValue: number = -1;
    @property({ attribute: false }) private _working: boolean = false;
    @property({ attribute: false }) private _allowEmptyInput: boolean = false;

    private async _readScheduleAndPlan(): Promise<void>
    {
        this._working = true;
        this._terminalId = "";
        this._inputValue = -1;
        this._allowEmptyInput = true;

        const executingPlan = await VesselPlanningModel!.getExecutingPlan();
        if (!executingPlan)
        {
            // Shouldn't happen
            this.dispatchEvent(new Event("close"));
            return;
        }

        const route = executingPlan.routes[executingPlan.selectedRouteIndex];
        if (this.wpPlanIndex < 0 || this.wpPlanIndex >= route.route_geometry.length)
        {
            // Wait for a proper wpPlanIndex
            return;
        }

        // Check for disallowed conditions
        if (this.wpPlanIndex === 0)
        {
            VoyageDialogPopupManager.createDialog({
                headerText: "Cannot set ETA on first waypoint",
                bodyText: "You cannot set an ETA on the first waypoint of a route"
            });
            this.dispatchEvent(new Event("close"));
            return;
        }

        if (VoyageRoutingModel!.planHasActiveCalculations(executingPlan.scheduleId))
        {
            VoyageDialogPopupManager.createDialog({
                headerText: "Calculation in progress",
                bodyText: "Calculation already in progress; Please wait for it to finish"
            });
            this.dispatchEvent(new Event("close"));
            return;
        }

        const currentPlan = await VesselPlanningModel!.getCurrentPlan(executingPlan.scheduleId);
        if (currentPlan!.status === "planning")
        {
            VoyageDialogPopupManager.createDialog({
                headerText: "Newer non-approved plan available",
                bodyText: "Please adjust the non-approved plan via the 'Edit Route' button instead"
            });
            this.dispatchEvent(new Event("close"));
            return;
        }

        // Use terminal local time for berth and departure/arrival pilots; UTC otherwise
        const waypoint = route.route_geometry[this.wpPlanIndex];
        if (this.wpPlanIndex === route.route_geometry.length-1)
        {
            // Changing last waypoint -> changing the plans ETA
            this._terminalId = executingPlan.arrivalTerminalCode;
            this._inputValue = executingPlan.eta;
            this._allowEmptyInput = false;
        }
        else if (waypoint.properties.pilotPoint)
        {
            // Check if it is the first or last pilot point
            const pilotIndeces: number[] = [];
            for (let i = 0; i < route.route_geometry.length; i++)
            {
                const feature = route.route_geometry[i];
                if (feature.geometry.type === "Point" && feature.properties.pilotPoint)
                {
                    pilotIndeces.push(i);
                }
            }

            if (this.wpPlanIndex === pilotIndeces[pilotIndeces.length-1])
            {
                this._terminalId = executingPlan.arrivalTerminalCode;
            }
            else if (this.wpPlanIndex === pilotIndeces[0])
            {
                this._terminalId = executingPlan.departureTerminalCode;
            }
        }

        if (waypoint.properties.eta)
        {
            this._inputValue = new Date(waypoint.properties.eta).getTime();
        }

        this._working = false;
    }

    private async _applyChange(): Promise<void>
    {
        this._working = true;
        let reCalcNeeded = false;
        try
        {
            const plan = deepCopy(await VesselPlanningModel!.getExecutingPlan());
            const waypoint = <VoyageRoutePointFeature>plan!.routes[plan!.selectedRouteIndex].route_geometry[this.wpPlanIndex];

            if (this._terminalId !== "")
            {
                // Changing a pilot point or arrival at berth
                if (this.wpPlanIndex === plan!.routes[plan!.selectedRouteIndex].route_geometry.length-1)
                {
                    // Changing arrival at berth
                    if (this._inputValue !== plan!.eta)
                    {
                        // Also update schedule
                        const scheduleEntry = await this._getScheduleEntry(plan!.scheduleId);
                        if (scheduleEntry)
                        {
                            const newPortCall = deepCopy(scheduleEntry.arrivalPortCall!.current)
                            newPortCall.arrivalAt = new Date(this._inputValue).toISOString();
                            newPortCall.estimatedpilotboarding = scheduleEntry!.arrivalPortCall!.current.estimatedpilotboarding;
                            newPortCall.estimatedpilotdropoff = scheduleEntry!.arrivalPortCall!.current.estimatedpilotdropoff;
                            verifyETAOverlapWithDepartureTime(this._inputValue, scheduleEntry, newPortCall);
                            await VoyageSchedulesModel!.changePortCall(newPortCall);    
                        }
                        else
                        {
                            // Should never happen
                            console.warn("No schedule entry found for ", plan!.scheduleId);
                        }

                        plan!.eta = this._inputValue;
                        reCalcNeeded = true;
                    }                        
                }
                else if (this._terminalId === plan!.departureTerminalCode)
                {
                    // Changing departure pilot ETA
                    if (this._inputValue === -1)
                    {
                        if (waypoint.properties.eta)
                        {
                            delete waypoint.properties.eta;
                            plan!.departurePilotEtd = -1;
                            reCalcNeeded = true;
                        }
                    }
                    else if (!waypoint.properties.eta || this._inputValue !== new Date(waypoint.properties.eta).getTime())
                    {
                        waypoint.properties.eta = new Date(this._inputValue).toISOString();
                        plan!.departurePilotEtd = this._inputValue;
                        reCalcNeeded = true;
                    }
                }
                else
                {
                    // Changing arrival pilot ETA
                    if (this._inputValue === -1)
                    {
                        if (waypoint.properties.eta)
                        {
                            delete waypoint.properties.eta;
                            plan!.arrivalPilotEta = -1;
                            reCalcNeeded = true;
                        }
                    }
                    else if (!waypoint.properties.eta || this._inputValue !== new Date(waypoint.properties.eta).getTime())
                    {
                        waypoint.properties.eta = new Date(this._inputValue).toISOString();
                        plan!.arrivalPilotEta = this._inputValue;
                        reCalcNeeded = true;
                    }
                }
            }
            else
            {
                // Changing ETA for non-pilot waypoint
                if (this._inputValue === -1)
                {
                    if (waypoint.properties.eta)
                    {
                        delete waypoint.properties.eta;
                        reCalcNeeded = true;
                    }
                }
                else if (!waypoint.properties.eta || this._inputValue !== new Date(waypoint.properties.eta).getTime())
                {
                    waypoint.properties.eta = new Date(this._inputValue).toISOString();
                    reCalcNeeded = true;
                }
            }
        
            if (plan && reCalcNeeded)
            {
                await recalcRoutes(plan);
            }

            this._working = false;
            this.dispatchEvent(new Event("close"));
        }
        catch (err)
        {
            this._working = false;
            console.warn("Updating schedule and/or plan failed: ", err)
            VoyageDialogPopupManager.createDialog({
                headerText: "Failed to update",
                bodyText: "Unknown error occurred when applying the change",
                isError: true,
                errorDetails: (err instanceof Error) ? err.message : String(err)
            });
        }
    }

    private async _getScheduleEntry(scheduleId: string): Promise<ScheduleEntry|null>
    {
        const schedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!) || [];
        for (const entry of schedule)
        {
            if (entry.id === scheduleId)
            {
                return entry;
            }
        }
        return null;
    }
};
